// Config for the interactive front/back anatomical body model + 4-color strain scale.
export const ZONE_LABELS = {
  head_neck: "Head / Neck",
  shoulders: "Shoulders",
  lower_back: "Lower Back",
  hips_groin: "Hips / Groin",
  quads: "Quads",
  hamstrings: "Hamstrings",
  knees: "Knees",
  calves: "Calves",
  ankles: "Ankles",
  feet: "Feet",
};

export const STRAIN_LEVELS = [
  { key: "green", label: "Ready", short: "Ready", color: "#22C55E", desc: "No pain — good to go" },
  { key: "yellow", label: "Low Tightness", short: "Tight", color: "#EAB308", desc: "Slight tightness" },
  { key: "orange", label: "Medium Pain", short: "Pain", color: "#F97316", desc: "Noticeable pain" },
  { key: "red", label: "High Pain", short: "High", color: "#EF4444", desc: "Sharp / high pain" },
];

export const LEVEL_COLOR = STRAIN_LEVELS.reduce((a, l) => ({ ...a, [l.key]: l.color }), {});
export const ZONE_ORDER = Object.keys(ZONE_LABELS);

// Which anatomical view each zone lives on.
export const ZONE_VIEW = {
  head_neck: "front", shoulders: "front", hips_groin: "front", quads: "front",
  knees: "front", ankles: "front", feet: "front",
  lower_back: "back", hamstrings: "back", calves: "back",
};

// viewBox 0 0 220 470. Neutral body silhouette (non-interactive), shared by both views.
export const BODY_BASE = [
  { t: "c", cx: 110, cy: 36, r: 24 },
  { t: "r", x: 100, y: 54, w: 20, h: 18, rx: 8 },
  { t: "p", d: "M72 96 Q70 74 110 74 Q150 74 148 96 L144 168 Q142 184 110 186 Q78 184 76 168 Z" },
  { t: "e", cx: 68, cy: 96, rx: 24, ry: 15 },
  { t: "e", cx: 152, cy: 96, rx: 24, ry: 15 },
  { t: "e", cx: 54, cy: 132, rx: 11, ry: 36 },
  { t: "e", cx: 166, cy: 132, rx: 11, ry: 36 },
  { t: "e", cx: 48, cy: 196, rx: 9, ry: 32 },
  { t: "e", cx: 172, cy: 196, rx: 9, ry: 32 },
  { t: "e", cx: 46, cy: 232, rx: 8, ry: 11 },
  { t: "e", cx: 174, cy: 232, rx: 8, ry: 11 },
  { t: "p", d: "M78 180 L142 180 Q153 180 152 195 L150 214 Q148 228 128 230 L92 230 Q72 228 70 214 L68 195 Q67 180 78 180 Z" },
  { t: "e", cx: 93, cy: 270, rx: 20, ry: 58 },
  { t: "e", cx: 127, cy: 270, rx: 20, ry: 58 },
  { t: "e", cx: 93, cy: 380, rx: 14, ry: 54 },
  { t: "e", cx: 127, cy: 380, rx: 14, ry: 54 },
  { t: "p", d: "M80 430 Q79 423 89 423 L103 424 Q109 425 108 437 L107 448 Q80 450 79 440 Z" },
  { t: "p", d: "M140 430 Q141 423 131 423 L117 424 Q111 425 112 437 L113 448 Q140 450 141 440 Z" },
];

// Interactive muscle groups, per view.
export const FRONT_ZONES = {
  head_neck: [{ t: "c", cx: 110, cy: 36, r: 21 }, { t: "r", x: 102, y: 54, w: 16, h: 16, rx: 7 }],
  shoulders: [{ t: "e", cx: 70, cy: 96, rx: 20, ry: 13 }, { t: "e", cx: 150, cy: 96, rx: 20, ry: 13 }],
  hips_groin: [{ t: "p", d: "M84 186 L136 186 Q146 186 145 199 L143 212 Q141 224 124 226 L96 226 Q79 224 77 212 L75 199 Q74 186 84 186 Z" }],
  quads: [{ t: "e", cx: 93, cy: 262, rx: 17, ry: 44 }, { t: "e", cx: 127, cy: 262, rx: 17, ry: 44 }],
  knees: [{ t: "e", cx: 93, cy: 314, rx: 14, ry: 12 }, { t: "e", cx: 127, cy: 314, rx: 14, ry: 12 }],
  ankles: [{ t: "e", cx: 93, cy: 414, rx: 10, ry: 9 }, { t: "e", cx: 127, cy: 414, rx: 10, ry: 9 }],
  feet: [
    { t: "p", d: "M80 430 Q79 423 89 423 L103 424 Q109 425 108 437 L107 448 Q80 450 79 440 Z" },
    { t: "p", d: "M140 430 Q141 423 131 423 L117 424 Q111 425 112 437 L113 448 Q140 450 141 440 Z" },
  ],
};

export const BACK_ZONES = {
  lower_back: [{ t: "p", d: "M84 118 L136 118 Q146 118 145 132 L143 156 Q141 172 110 174 Q79 172 77 156 L75 132 Q74 118 84 118 Z" }],
  hamstrings: [{ t: "e", cx: 93, cy: 288, rx: 17, ry: 42 }, { t: "e", cx: 127, cy: 288, rx: 17, ry: 42 }],
  calves: [{ t: "e", cx: 93, cy: 366, rx: 14, ry: 36 }, { t: "e", cx: 127, cy: 366, rx: 14, ry: 36 }],
};

// Fallback badge centers for path-only zones (heat mode).
export const ZONE_CENTER = { lower_back: [110, 146], hips_groin: [110, 205] };

export function heatFill(counts) {
  if (!counts) return null;
  if (counts.red > 0) return "#EF4444";
  if (counts.orange > 0) return "#F97316";
  if (counts.yellow > 0) return "#EAB308";
  return null;
}
